import { createElement } from "react";
import { cn } from "~/lib/utils";

type TextBaseProps = React.ComponentProps<"p"> & {
  as?: "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "span";
  medium?: boolean;
  semibold?: boolean;
  bold?: boolean;
  italic?: boolean;
  center?: boolean;
  gray?: boolean;
  primary?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
};

function TextBase({
  as = "p",
  children,
  className,
  medium = false,
  semibold = false,
  bold = false,
  italic = false,
  center = false,
  primary = false,
  gray = false,
  secondary = false,
  tertiary = false,
  ...props
}: TextBaseProps) {
  return createElement(
    as,
    {
      className: cn(
        medium ? "font-medium" : "",
        semibold ? "font-semibold" : "",
        bold ? "font-bold" : "",
        italic ? "italic" : "",
        center ? "text-center" : "",
        gray ? "text-gray-500" : "",
        primary ? "text-primary-600" : "",
        secondary ? "text-secondary-800" : "",
        tertiary ? "text-tertiary-600" : "",
        className
      ),
      ...props,
    },
    children
  );
}

function H1({ children, className, ...props }: TextBaseProps) {
  return (
    <TextBase as="h1" className={cn("font-sans-tight text-3xl", className)} medium={true} {...props}>
      {children}
    </TextBase>
  );
}

function H2({ children, className, ...props }: TextBaseProps) {
  return (
    <TextBase as="h2" className={cn("font-sans-tight text-2xl", className)} medium={true} {...props}>
      {children}
    </TextBase>
  );
}

function H3({ children, className, ...props }: TextBaseProps) {
  return (
    <TextBase as="h3" className={cn("font-sans-tight text-xl", className)} {...props}>
      {children}
    </TextBase>
  );
}

function H4({ children, className, ...props }: TextBaseProps) {
  return (
    <TextBase as="h4" className={cn("font-sans-tight text-lg", className)} {...props}>
      {children}
    </TextBase>
  );
}

function H5({ children, className, ...props }: TextBaseProps) {
  return (
    <TextBase as="h5" className={cn("font-sans-tight text-base", className)} {...props}>
      {children}
    </TextBase>
  );
}

function H6({ children, className, ...props }: TextBaseProps) {
  return (
    <TextBase as="h6" className={cn("font-sans-tight text-sm", className)} {...props}>
      {children}
    </TextBase>
  );
}

function P1({ children, className, ...props }: TextBaseProps) {
  return (
    <TextBase as="p" className={cn("font-sans text-base", className)} {...props}>
      {children}
    </TextBase>
  );
}

function P2({ children, className, ...props }: TextBaseProps) {
  return (
    <TextBase as="p" className={cn("font-sans text-sm", className)} {...props}>
      {children}
    </TextBase>
  );
}

function P3({ children, className, ...props }: TextBaseProps) {
  return (
    <TextBase as="p" className={cn("font-sans text-xs", className)} {...props}>
      {children}
    </TextBase>
  );
}

function Span({ children, className, ...props }: TextBaseProps) {
  return (
    <TextBase as="span" className={cn(className)} {...props}>
      {children}
    </TextBase>
  );
}

export { H1, H2, H3, H4, H5, H6, P1, P2, P3, Span };
